import { useState, useEffect, useMemo } from 'react';
import { EventSeries } from '../types/eventSeries.types';
import { locationCache } from '../services/LocationCacheService';
import { smartListenerService } from '../supabase/SmartListenerService';
import { supabaseEventSeriesService } from '../supabase/eventSeries.service';
import { computeSeriesEndDate, computeSeriesPattern } from '../utils/seriesPatternUtils';

interface UseSmartSeriesResult {
  series: EventSeries | null;
  loading: boolean;
  error: Error | null;
  cacheSource: 'indexedDB' | 'preview' | 'none';
  pattern: string | null;
  endDate: Date | undefined;
}

/**
 * 🔄 HOOK SÉRIES UNIFIÉ - Version SmartCache + Pattern Formatting
 * Récupère les séries d'événements depuis IndexedDB et calcule le pattern de récurrence
 *
 * ⚠️ Ce hook crée un abonnement Realtime si organizerId est fourni.
 * Pour la HomePage touriste, utiliser useSeriesBatch à la place (batch sans Realtime).
 *
 * @param seriesId - ID de la série à charger depuis IndexedDB
 * @param previewData - Données de prévisualisation (pour EventForm)
 * @param organizerId - ID organisateur pour déclencher le listener (optionnel)
 * @param seriesEvents - Occurrences déjà chargées (pour fallback endDate quand série sans config de fin)
 */
export const useSmartSeries = (
  seriesId?: string,
  previewData?: EventSeries,
  organizerId?: string,
  seriesEvents?: any[]
): UseSmartSeriesResult => {
  const [cachedSeries, setCachedSeries] = useState<EventSeries | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [cacheSource, setCacheSource] = useState<'indexedDB' | 'preview' | 'none'>('none');

  // Utiliser previewData en priorité, sinon les données du cache
  const series = previewData || cachedSeries;
  const finalCacheSource = previewData ? 'preview' : cacheSource;
  const finalLoading = !previewData && loading;

  useEffect(() => {
    if (!seriesId || previewData) {
      console.log('🔄 [useSmartSeries] Pas de seriesId ou preview mode, reset');
      setCachedSeries(null);
      setCacheSource(previewData ? 'preview' : 'none');
      return;
    }

    const loadSeries = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('🔄 [useSmartSeries] Chargement série depuis IndexedDB:', seriesId);

        // Récupérer la série depuis IndexedDB
        const cachedData = await locationCache.getSeries(seriesId);

        if (cachedData) {
          console.log('💾 [useSmartSeries] IndexedDB HIT pour série:', seriesId);

          // Convertir les dates Firestore si nécessaire
          const normalizedSeries: EventSeries = {
            ...cachedData,
            createdAt: cachedData.createdAt instanceof Date
              ? cachedData.createdAt
              : new Date(cachedData.createdAt),
            updatedAt: cachedData.updatedAt instanceof Date
              ? cachedData.updatedAt
              : new Date(cachedData.updatedAt)
          };

          setCachedSeries(normalizedSeries);
          setCacheSource('indexedDB');
        } else {
          // Cache miss - Fallback vers Supabase pour récupérer la série
          console.log('📭 [useSmartSeries] Cache miss, fallback Supabase pour:', seriesId);

          try {
            const supabaseSeries = await supabaseEventSeriesService.getSeriesById(seriesId);

            if (supabaseSeries) {
              console.log('✅ [useSmartSeries] Série récupérée depuis Supabase:', supabaseSeries.title);

              // Sauvegarder en cache pour les prochains accès
              await locationCache.saveSeries(supabaseSeries);

              setCachedSeries(supabaseSeries);
              setCacheSource('indexedDB');
            } else {
              console.log('📭 [useSmartSeries] Série non trouvée en cache ni Supabase:', seriesId);
              setCachedSeries(null);
              setCacheSource('none');
            }
          } catch (supabaseErr) {
            console.error('❌ [useSmartSeries] Erreur fallback Supabase:', supabaseErr);
            setCachedSeries(null);
            setCacheSource('none');
          }
        }

      } catch (err) {
        console.error('❌ [useSmartSeries] Erreur chargement série:', err);
        setError(err as Error);
        setCachedSeries(null);
        setCacheSource('none');
      } finally {
        setLoading(false);
      }
    };

    // 🚀 S'ABONNER AU LISTENER SÉRIES (si organizerId fourni)
    let unsubscribe: (() => void) | null = null;

    if (organizerId) {
      const consumerId = `useSmartSeries-${seriesId}`;
      console.log(`🔔 [useSmartSeries] Abonnement listener séries ${seriesId} (org: ${organizerId})`);

      unsubscribe = smartListenerService.subscribeToSeries(
        organizerId,
        consumerId,
        (seriesList: EventSeries[]) => {
          console.log(`🔄 [useSmartSeries] Reçu ${seriesList.length} séries du listener`);
          const targetSeries = seriesList.find(s => s.id === seriesId);
          if (targetSeries) {
            console.log(`✅ [useSmartSeries] Série ${seriesId} trouvée via listener`);
            setCachedSeries(targetSeries);
            setCacheSource('indexedDB');
            setLoading(false);
          } else {
            console.log(`❌ [useSmartSeries] Série ${seriesId} non trouvée dans les ${seriesList.length} séries reçues`);
          }
        }
      );
    }

    // Chargement initial depuis IndexedDB
    loadSeries();

    // Cleanup
    return () => {
      if (unsubscribe) {
        console.log(`🧹 [useSmartSeries] Désabonnement listener pour série ${seriesId}`);
        unsubscribe();
      }
    };
  }, [seriesId, previewData, organizerId]);

  // Calcul endDate via config série, avec fallback vers les occurrences réelles
  const endDate = useMemo(() => {
    if (!series) return undefined;

    // Essayer le calcul depuis la config série
    const computed = computeSeriesEndDate(series);
    if (computed) return computed;

    // Fallback: dernière occurrence depuis les seriesEvents passés en param
    if (seriesEvents && seriesEvents.length > 0) {
      const lastEvent = seriesEvents
        .filter((e: any) => e.datetime)
        .sort((a: any, b: any) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime())[0];
      if (lastEvent?.datetime) {
        return new Date(lastEvent.datetime);
      }
    }

    return undefined;
  }, [series, seriesEvents]);

  const pattern = useMemo(() => {
    if (!series) return null;
    return computeSeriesPattern(series, endDate);
  }, [series, endDate]);

  return {
    series,
    loading: finalLoading,
    error,
    cacheSource: finalCacheSource,
    pattern,
    endDate
  };
};
